/**
 * CharacterDesignerNode — V2 (Sprint 9i migration)
 *
 * Migrated to use UnifiedBottomPanel with 4 tabs:
 * Config, Refine, Variants, Voice
 *
 * Uses DesignerNodeShell for shared UI (border, image, lightbox, error, handles, toolbar).
 * Unique features: editable name/description, voice section, Quick Info Bar.
 *
 * Width: 400px. Image aspect: 16:9 (aspect-video).
 */

import React, { memo, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Position, NodeProps, useEdges, useReactFlow } from 'reactflow'
import { getThumbnailUrl, getAssetUrl, createNadDebug, type CharacterDesignerNodeData } from '@ponpon/api-client'
import { useExecuteNodeAction } from '../../../../hooks/useExecuteNodeAction'
import { useCanvasOptional } from '../../CanvasContext'
import type { ReactFlowCanvasEdgeData, ReactFlowNodeData } from '../../../../hooks/useYCanvas'
import {
  DesignerNodeShell,
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  UnifiedBottomPanel,
  CHARACTER_TABS,
  DEFAULT_TAB,
  ConfigTabContent,
    RefineTabContent,
  VariantsTabContent,
  VoiceTabContent,
  ShimmerPlaceholder,
  TypewriterText,
  useSequencedLoading,
  useDesignerVariantSelection,
  useBottomPanelAutoOpen,
  resolveDesignerImagePath,
  createHandlesFromSchema,
  type SelectedReference,
  buildAssetReferencesFromEdges,
  buildImageReferenceEdgeUpdates,
  type HistoryEntry,
} from '../shared'
import { useFeedbackMode } from '../../panels/useFeedbackMode'
import { AGENT_COLORS } from '../../../../constants/agentColors'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import characterDesignerSchema from '../../../../../../../schemas/nodes/characterDesigner.json'
import { NODE_BG_SWATCH_COLORS } from '../shared/nodeBgColors'

export type { CharacterDesignerNodeData }

const HANDLES = createHandlesFromSchema(characterDesignerSchema, {
  inputDefaults: { position: Position.Left },
  outputDefaults: { position: Position.Bottom },
  inputs: {
    in_upstream: { position: Position.Top },
  },
  outputs: {
    out_name: { position: Position.Right, style: { top: '35%' } },
    out_voice_profile: { position: Position.Right },
  },
})

const agentColor = AGENT_COLORS.character

const CHARACTER_EMPTY_ICON_PATH =
  'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'

const VOICE_REGEN_PROGRESS_MESSAGE = 'Voice being generated...'
const voiceRegenLog = createNadDebug('dw:voice-regeneration-ui')

const CharacterDesignerNode = memo(function CharacterDesignerNode(
  props: NodeProps<CharacterDesignerNodeData>
) {
  const { id, data, selected, dragging } = props
  const {
    cacheKey,
    in_name = '',
    in_role = '',
    in_style,
    in_model = 'openai-gpt-image-2',
    in_description = '',
    out_character_sheet,
    out_voice_profile,
    status = 'idle',
    progressMessage,
    error,
  } = data
  const projectId = useProjectId()

  const { setNodes, getNodes } = useReactFlow<ReactFlowNodeData, ReactFlowCanvasEdgeData>()
  const edges = useEdges<ReactFlowCanvasEdgeData>()
  const canvas = useCanvasOptional()

  const isRunning = status === 'running'

  // Image path resolution (shared utility)
  const imagePath = resolveDesignerImagePath(out_character_sheet, data.picked_variant_index)

  // Sequenced loading animation
  const {
    showSubtitleShimmer,
    subtitleActive,
    onSubtitleComplete,
  } = useSequencedLoading({ isRunning })

  // Bottom panel state
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)
  const voiceRegenRunningSeenRef = useRef(false)
  const voiceRegenRunningLoggedRef = useRef(false)
  const voiceRegenTraceIdRef = useRef<string | null>(null)

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      setNodes((nodes) => nodes.map((n) => ({ ...n, selected: n.id === id })))
    },
    [id, setNodes]
  )

  // History state
  const mainImageHistory = out_character_sheet?.images?.main
  const historyEntries = useMemo(
    (): HistoryEntry[] => mainImageHistory?.history ?? [],
    [mainImageHistory?.history]
  )
  const pickedIndex = data.picked_variant_index ?? mainImageHistory?.picked_index ?? 0
  const batchPending = isRunning ? (data.batch_pending ?? 0) : 0

  // Shared auto-open behavior
  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })

  // Feedback mode
  const { feedbackText, setFeedbackText, handleRefine } = useFeedbackMode({
    nodeId: id,
    status,
  })

  // Shared variant selection (updates picked_index)
  const handleHistorySelect = useDesignerVariantSelection({
    nodeId: id,
    historyEntries,
    outputFieldName: 'out_character_sheet',
  })

  // Voice data
  const voiceName = out_voice_profile?.voice_name
  const voiceSampleCacheKey = out_voice_profile
    ? [
        out_voice_profile.provider,
        out_voice_profile.voice_id,
        out_voice_profile.voice_name,
        out_voice_profile.audio_sample,
        out_voice_profile.speech_text,
      ].filter(Boolean).join(':')
    : undefined
  const voiceSampleUrl =
    out_voice_profile?.audio_sample && projectId
      ? getAssetUrl(projectId, out_voice_profile.audio_sample, 0, voiceSampleCacheKey)
      : null

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newDesc = e.target.value
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: newDesc } } : n
        )
      )
      canvas?.updateNodeData(id, { in_description: newDesc })
    },
    [id, setNodes, canvas]
  )

  // Config tab: generate handler — uses useExecuteNodeAction for the
  // 1s cooldown + insufficient-sparks modal handling.
  const { runExecute, isLocked: generateLocked, cancelExecution, isCancelling } = useExecuteNodeAction({
    projectId,
    nodeId: id,
    isRunning,
  })
  const handleConfigGenerate = useCallback(
    (retryCount: number) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, progressMessage: undefined } } : n
        )
      )
      canvas?.updateNodeData(id, { progressMessage: undefined })
      runExecute(retryCount)
    },
    [canvas, id, runExecute, setNodes],
  )

  // Refine handler
  const onRefineSubmit = useCallback(() => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, progressMessage: undefined } } : n
      )
    )
    canvas?.updateNodeData(id, { progressMessage: undefined })
    handleRefine()
  }, [canvas, handleRefine, id, setNodes])

  // Voice regeneration — uses useExecuteNodeAction with action='regenerate_voice'
  // for cooldown + insufficient-sparks handling (same guardrails as Generate).
  const { runExecute: runVoiceRegen, isLocked: voiceRegenLocked } = useExecuteNodeAction({
    projectId,
    nodeId: id,
    isRunning,
    action: 'regenerate_voice',
  })
  const handleRegenerateVoice = useCallback(() => {
    const traceId = `voice-regen:${id}:${Date.now()}`
    voiceRegenTraceIdRef.current = traceId
    setActiveTab('voice')
    setBottomPanelOpen(true)
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? { ...n, data: { ...n.data, progressMessage: VOICE_REGEN_PROGRESS_MESSAGE } }
          : n
      )
    )
    canvas?.updateNodeData(id, { progressMessage: VOICE_REGEN_PROGRESS_MESSAGE })
    voiceRegenLog({
      event: 'regenerate-voice-clicked',
      trace_id: traceId,
      nodeId: id,
      activeTab: 'voice',
      progressMessage: VOICE_REGEN_PROGRESS_MESSAGE,
    })
    runVoiceRegen()
  }, [canvas, id, runVoiceRegen, setNodes])

  useEffect(() => {
    if (progressMessage !== VOICE_REGEN_PROGRESS_MESSAGE) {
      voiceRegenRunningSeenRef.current = false
      voiceRegenRunningLoggedRef.current = false
      voiceRegenTraceIdRef.current = null
      return
    }
    if (isRunning) {
      voiceRegenRunningSeenRef.current = true
      if (!voiceRegenRunningLoggedRef.current) {
        voiceRegenLog({
          event: 'regenerate-voice-running-state',
          trace_id: voiceRegenTraceIdRef.current ?? `voice-regen:${id}:unknown`,
          nodeId: id,
          activeTab,
          batchPending,
          progressMessage,
        })
        voiceRegenRunningLoggedRef.current = true
      }
      return
    }
    if (!voiceRegenRunningSeenRef.current) return
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, progressMessage: undefined } } : n
      )
    )
    canvas?.updateNodeData(id, { progressMessage: undefined })
    voiceRegenRunningSeenRef.current = false
    voiceRegenRunningLoggedRef.current = false
    voiceRegenTraceIdRef.current = null
  }, [activeTab, batchPending, canvas, id, isRunning, progressMessage, setNodes])

  // Style change handler
  const handleStyleChange = useCallback(
    (styleId: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_style: styleId } } : n
        )
      )
      canvas?.updateNodeData(id, { in_style: styleId })
    },
    [id, setNodes, canvas]
  )

  // Model change handler
  const handleModelChange = useCallback(
    (model: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_model: model } } : n
        )
      )
      canvas?.updateNodeData(id, { in_model: model })
    },
    [id, setNodes, canvas]
  )

  // Reference handling for ConfigTabContent
  const currentRefs = useMemo<SelectedReference[]>(() => {
    return buildAssetReferencesFromEdges(getNodes(), edges, id, 'in_images', 'image')
  }, [edges, getNodes, id])

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      if (canvas) {
        canvas.replacePortEdges(id, 'in_images', buildImageReferenceEdgeUpdates(refs))
      }
    },
    [id, canvas]
  )

  // Prompt change handler
  const handlePromptChange = useCallback(
    (value: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: value } } : n
        )
      )
      canvas?.updateNodeData(id, { in_description: value })
    },
    [id, setNodes, canvas]
  )

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_name: newName } } : n
        )
      )
      canvas?.updateNodeData(id, { in_name: newName })
    },
    [id, setNodes, canvas],
  )

  const handleBgColorChange = useCallback(
    (colorKey: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_bg_color: colorKey } } : n,
        ),
      )
      canvas?.updateNodeData(id, { in_bg_color: colorKey })
    },
    [id, setNodes, canvas],
  )

  // Cache bust for reference image thumbnails
  const cacheBust = cacheKey

  // Panel visibility: selected AND not running AND not dragging AND panel open
  // Exception: during regeneration, auto-open to Variants (handled by useBottomPanelAutoOpen)
  const panelVisible = !!selected && !dragging && bottomPanelOpen 

  // Bottom panel (rendered via bottomPanel prop to avoid overflow-hidden clipping)
  const bottomPanel = (
    <UnifiedBottomPanel
      tabs={CHARACTER_TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      isVisible={panelVisible}
    >
      {(tab) => {
        if (tab === 'config' && projectId) {
          return (
            <ConfigTabContent
              nodeId={id}
              nodeType="character"
              prompt={in_description}
              onPromptChange={handlePromptChange}
              references={currentRefs}
              onReferencesChange={handleReferencesChange}
              currentStyleId={in_style}
              onStyleChange={handleStyleChange}
              model={in_model}
              onModelChange={handleModelChange}
              isRunning={isRunning || generateLocked}
              onGenerate={handleConfigGenerate}
              onCancel={cancelExecution}
              isCancelling={isCancelling}
            />
          )
        }
        if (tab === 'refine') {
          return (
            <RefineTabContent
              feedbackText={feedbackText}
              onFeedbackTextChange={setFeedbackText}
              onSubmit={onRefineSubmit}
              isRunning={isRunning}
              nodeType="characterDesigner"
              selectedStyleId={in_style}
              onStyleChange={handleStyleChange}
            />
          )
        }
        if (tab === 'variants') {
          return (
            <VariantsTabContent
              history={historyEntries}
              pickedIndex={pickedIndex}
              onSelectVariant={handleHistorySelect}
              batchPending={batchPending}
              cacheKey={cacheBust}
              projectId={projectId}
              accentColor="purple"
              aspectRatio="16/9"
            />
          )
        }
        if (tab === 'voice') {
          return (
            <VoiceTabContent
              voiceName={voiceName}
              voiceSampleUrl={voiceSampleUrl}
              speechText={out_voice_profile?.speech_text}
              onRegenerateVoice={handleRegenerateVoice}
              isRunning={isRunning || voiceRegenLocked}
              hasCharacterSheet={!!out_character_sheet}
            />
          )
        }
        return null
      }}
    </UnifiedBottomPanel>
  )

  return (
    <DesignerNodeShell
      nodeId={id}
      selected={selected}
      dragging={dragging}
      status={status}
      error={error}
      progressMessage={progressMessage}
      nodeName={in_name}
      agentTypeLabel="Character"
      agentColor={agentColor}
      width={400}
      imageAspect="aspect-video"
      handles={HANDLES}
      testId="character-designer-node"
      imagePath={imagePath}
      cacheKey={cacheKey}
      emptyIconPath={CHARACTER_EMPTY_ICON_PATH}
      onCancel={cancelExecution}
      isCancelling={isCancelling}
      imageHistory={historyEntries}
      pickedIndex={pickedIndex}
      bottomPanel={bottomPanel}
      floatingLabel={<FloatingNodeLabel name={in_name} fallback="Untitled Character" typeIcon={NODE_TYPE_ICON.characterDesigner} onRename={handleRename} bgColor={data.in_bg_color && data.in_bg_color !== 'default' ? NODE_BG_SWATCH_COLORS[data.in_bg_color] : undefined} />}
      bgColorKey={data.in_bg_color}
      onBgColorChange={handleBgColorChange}
    >
      {/* Content Area */}
      <div className="pt-2 px-6 pb-6">
        {/* Role label */}
        <span className="text-xs font-medium" style={{ color: agentColor.active }}>
          {in_role || 'Character'}
        </span>

        {/* Description -- editable or animated */}
        <div className="mt-2 h-[60px]">
          {showSubtitleShimmer ? (
            <div className="space-y-1.5">
              <ShimmerPlaceholder width="100%" height={14} />
              <ShimmerPlaceholder width="80%" height={14} />
            </div>
          ) : isRunning && in_description ? (
            <TypewriterText
              text={in_description}
              isActive={subtitleActive}
              onComplete={onSubtitleComplete}
              className="text-sm text-muted-foreground leading-relaxed line-clamp-3"
              as="p"
            />
          ) : (
            <textarea
              data-testid="character-description-input"
              aria-label="Character description"
              value={in_description || ''}
              placeholder="Describe this character..."
              onChange={handleDescriptionChange}
              className="w-full h-full text-sm text-muted-foreground leading-relaxed bg-transparent border-none outline-none p-0 m-0 resize-none nodrag nowheel"
              rows={3}
            />
          )}
        </div>
      </div>

      {/* Quick Info Bar -- 2 columns: Config | Variants */}
      <div className="grid grid-cols-2 border-t border-border-subtle px-6">
        {/* Config Column */}
        <button
          data-testid="quick-info-config"
          onClick={(e) => {
            e.stopPropagation()
            openPanelToTab('config')
          }}
          className="py-3 flex flex-col items-start gap-1.5 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle pr-3"
        >
          <span className="text-sm font-medium text-foreground truncate">
            Config
          </span>
          {currentRefs.length > 0 && (
            <div className="flex items-center gap-1">
              {currentRefs.slice(0, 2).map((ref, i) => {
                const thumbUrl = projectId
                  ? getThumbnailUrl(projectId, ref.assetPath, 32, cacheBust)
                  : null
                return thumbUrl ? (
                  <div key={i} className="w-6 h-6 rounded overflow-hidden border border-border">
                    <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : null
              })}
            </div>
          )}
        </button>

        {/* Variants Column */}
        <button
          data-testid="quick-info-variants"
          onClick={(e) => {
            e.stopPropagation()
            openPanelToTab('variants')
          }}
          className="px-3 py-3 flex flex-col items-start gap-1.5 hover:bg-surface-1 transition-colors nodrag"
        >
          <span className="text-sm font-medium text-foreground">Variants</span>
          <div className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{
                backgroundColor:
                  historyEntries.length > 0 ? agentColor.active : 'var(--text-muted)',
              }}
            />
            <span className="text-sm font-medium text-foreground">
              {historyEntries.length}
            </span>
          </div>
        </button>
      </div>
    </DesignerNodeShell>
  )
})

CharacterDesignerNode.displayName = 'CharacterDesignerNode'
export default CharacterDesignerNode
